import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  Tooltip,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import type { CreditBatch } from "@taxassist/api-client";

interface CreditBatchesTableProps {
  batches: CreditBatch[];
  showExpired?: boolean;
}

/**
 * CreditBatchesTable displays credit batches in a structured table format.
 * Shows batch details, remaining credits, and expiry information with visual indicators.
 */
export function CreditBatchesTable({
  batches,
  showExpired = false,
}: CreditBatchesTableProps) {
  // Sort batches by expiry date (soonest first)
  const sortedBatches = [...batches].sort(
    (a, b) =>
      new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime(),
  );

  // Filter expired batches if needed
  const now = new Date();
  const filteredBatches = showExpired
    ? sortedBatches
    : sortedBatches.filter((batch) => new Date(batch.expiryDate) >= now);

  // Helper function to determine if a batch is expiring soon (within 30 days)
  const isExpiringSoon = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiry <= thirtyDaysFromNow && expiry >= now;
  };

  // Helper function to determine if a batch is expired
  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < now;
  };

  // Helper function to get row color based on status
  const getRowColor = (batch: CreditBatch) => {
    if (isExpired(batch.expiryDate)) return "error.light";
    if (isExpiringSoon(batch.expiryDate)) return "warning.light";
    return "background.paper";
  };

  const getExpiryChip = (batch: CreditBatch) => {
    const expired = isExpired(batch.expiryDate);
    const expiringSoon = isExpiringSoon(batch.expiryDate);

    if (expired) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Expired"
          color="error"
          size="small"
          variant="filled"
        />
      );
    }

    if (expiringSoon) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Expires Soon"
          color="warning"
          size="small"
          variant="filled"
        />
      );
    }

    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Active"
        color="success"
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const shortenBatchId = (batchId: string) => {
    // Show first 8 characters for readability
    return batchId.substring(0, 8) + "...";
  };

  if (filteredBatches.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {showExpired ? "No credit batches found" : "No active credit batches"}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table size="small" aria-label="credit batches table">
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 600 }}>Batch ID</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Credits
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Status
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Expires
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Created
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredBatches.map((batch) => (
            <TableRow
              key={batch.batchId}
              sx={{
                backgroundColor: getRowColor(batch),
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <TableCell>
                <Tooltip title={batch.batchId} arrow>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {shortenBatchId(batch.batchId)}
                  </Typography>
                </Tooltip>
              </TableCell>

              <TableCell align="center">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {batch.creditsRemaining}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {batch.creditsOriginal}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell align="center">{getExpiryChip(batch)}</TableCell>

              <TableCell align="center">
                <Typography
                  variant="body2"
                  color={
                    isExpired(batch.expiryDate)
                      ? "error.main"
                      : isExpiringSoon(batch.expiryDate)
                        ? "warning.main"
                        : "text.primary"
                  }
                  sx={{
                    fontWeight: isExpiringSoon(batch.expiryDate) ? 600 : 400,
                  }}
                >
                  {formatDate(batch.expiryDate)}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                  {formatDate(batch.createdAt)}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "monospace",
                  }}
                  title={batch.invoiceId || batch.reason || "N/A"}
                >
                  {batch.invoiceId || batch.reason || "N/A"}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
